// src/components/ui/RiskIQGaugeHC.tsx
import React, { useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import SolidGauge from "highcharts/modules/solid-gauge";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";

// ---- Safe module init (unchanged) ----
let __hcInit = false;
const initHC = (mod: any) => {
  const fn = mod && (mod as any).default ? (mod as any).default : mod;
  if (typeof fn === "function") fn(Highcharts as any);
};
if (!__hcInit) {
  initHC(HighchartsMore);
  initHC(SolidGauge);
  initHC(HighchartsExporting);
  initHC(HighchartsOfflineExporting);
  __hcInit = true;
}

type Props = {
  value: number; // 0..100
  size?: number; // 300 or 360 recommended
  onExplain?: () => void;
  leftLabel?: string;
  rightLabel?: string;
  onExportReady?: (exportMethods: ExportMethods) => void;
};

export type ExportMethods = {
  exportAsSVG: () => string | null;
  exportAsPNG: () => string | null;
  downloadPNG: (filename?: string) => void;
  downloadSVG: (filename?: string) => void;
};

export default function RiskIQGaugeHC({
  value,
  size = 360,
  leftLabel = "Very Low",
  rightLabel = "Very High",
  onExplain,
  onExportReady,
}: Props) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const pointerRef = useRef<Highcharts.SVGElement | null>(null);
  const customTicksRef = useRef<Highcharts.SVGElement[]>([]);
  const customTicksGroupRef = useRef<Highcharts.SVGElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const explainAnchorRef = useRef<HTMLElement | null>(null);
  const prevValRef = useRef<number>(0);

  // ---- Ring geometry: EXACTLY as before (thick) ----
  const OUTER = "97%";
  const INNER = "86%";

  // Custom inside minor ticks that skip 0/25/50/75/100 and endpoints
  function drawCustomInsideTicks(chart: Highcharts.Chart) {
    const paneAny = (chart as any).pane?.[0];
    const center: [number, number, number, number] | undefined = paneAny?.center;
    if (!center) return;
    const cx = chart.plotLeft + center[0];
    const cy = chart.plotTop + center[1];
    const Rp = center[2] / 2;
    const Rout = Rp * 0.97;
    const Rin = Rp * 0.86;
    const start = -120,
      end = 120;
    const toRad = (deg: number) => (Math.PI / 180) * deg;

    // ensure a dedicated, topmost group so ticks aren't clipped/hidden
    if (!customTicksGroupRef.current) {
      customTicksGroupRef.current = chart.renderer
        .g("custom-minor-ticks")
        .attr({ zIndex: 15 })
        .add();
    } else {
      // bring to front on re-render
      (customTicksGroupRef.current as any).toFront &&
        (customTicksGroupRef.current as any).toFront();
    }

    // cleanup previous tick elements
    customTicksRef.current.forEach((el) => el.destroy());
    customTicksRef.current = [];

    const axis: any = chart.yAxis && chart.yAxis[0];
    if (!axis || typeof axis.getPosition !== "function") return;
    for (let v = 5; v <= 95; v += 5) {
      if (v % 25 === 0) continue; // skip majors 25/50/75
      const len = 7;
      const outward = 5; // push visibly beyond the rim
      const p1 = axis.getPosition(v, Rout + outward);
      const p2 = axis.getPosition(v, Rout + outward + len);
      const seg = chart.renderer
        .path([
          ["M", p1.x, p1.y],
          ["L", p2.x, p2.y],
        ])
        .attr({ stroke: "rgba(255,255,255,0.28)", "stroke-width": 1 })
        .add(customTicksGroupRef.current as any);
      customTicksRef.current.push(seg);
    }
  }

  const options = useMemo<Highcharts.Options>(() => {
    const bands: Highcharts.YAxisPlotBandsOptions[] = [
      { from: 0, to: 20, color: "#FFF7C2", outerRadius: OUTER, innerRadius: INNER },
      { from: 20, to: 40, color: "#FFEFA5", outerRadius: OUTER, innerRadius: INNER },
      { from: 40, to: 60, color: "#FFE372", outerRadius: OUTER, innerRadius: INNER },
      { from: 60, to: 80, color: "#FFD73E", outerRadius: OUTER, innerRadius: INNER },
      { from: 80, to: 100, color: "#FFDF20", outerRadius: OUTER, innerRadius: INNER },
      // white separators spanning full ring thickness
      { from: 19.7, to: 20.3, color: "#ffffff", outerRadius: OUTER, innerRadius: INNER },
      { from: 39.7, to: 40.3, color: "#ffffff", outerRadius: OUTER, innerRadius: INNER },
      { from: 59.7, to: 60.3, color: "#ffffff", outerRadius: OUTER, innerRadius: INNER },
      { from: 79.7, to: 80.3, color: "#ffffff", outerRadius: OUTER, innerRadius: INNER },
    ];

    // hard horizontal centering for the center label
    const xAdjust = 0;

    return {
      chart: {
        type: "gauge",
        backgroundColor: "transparent",
        height: size,
        width: size,
        spacing: [0, 0, 0, 0],
        events: {
          load: function () {
            drawCustomInsideTicks(this as unknown as Highcharts.Chart);
          },
          render: function () {
            drawCustomInsideTicks(this as unknown as Highcharts.Chart);
          },
        },
      },
      title: undefined,
      credits: { enabled: false },
      
      // Disable default export menu button
      exporting: {
        enabled: false,
      },

      // turn off accessibility module warnings
      accessibility: {
        enabled: false,
      },

      pane: {
        startAngle: -120,
        endAngle: 120,
        size: "85%",
        center: ["50%", "50%"],
        background: [
          {
            outerRadius: OUTER,
            innerRadius: INNER,
            shape: "arc",
            borderWidth: 0,
            backgroundColor: "transparent",
          },
        ],
      },

      // Axis 0 paints the colored ring
      // Axis 1 draws the numbers only; we disabled built-in minor dashes and draw our own
      yAxis: [
        {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickWidth: 0,
          gridLineWidth: 0,
          minorTicks: false,
          labels: { enabled: false },
          plotBands: bands,
        },
        {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickWidth: 0,
          gridLineWidth: 0,
          tickLength: 0,
          minorTicks: false,
          offset: 0,
          labels: {
            distance: 8,
            style: {
              color: "#E6EDF5",
              fontSize: "11px",
              fontFamily: "var(--font-barlow), ui-sans-serif",
            },
          },
          tickPositions: [0, 25, 50, 75, 100],
          // built-in minors disabled; custom minor ticks are drawn via renderer
        },
      ],

      plotOptions: {
        gauge: {
          // move the value stack further up into the arc (ONLY positioning change)
          dataLabels: {
            enabled: true,
            useHTML: true,
            borderWidth: 0,
            y: -(Math.round(size * 0.1) + 44),
            align: "center",
            x: xAdjust,
            style: { textOutline: "none", textAlign: "center" },
            formatter: function () {
              const big = `<div style="font-size:${Math.round(
                size * 0.24,
              )}px;font-weight:700;color:#F7FAFF;line-height:1;font-family:var(--font-barlow),ui-sans-serif">${clamped}</div>`;
              // NOTE: removed data-explain attribute to avoid Highcharts #33
              const link =
                '<div style="margin-top:6px;color:#E6EDF5;font-size:13px;font-family:var(--font-barlow),ui-sans-serif"><a class="riskiq-explain-link" href="#" style="text-decoration:underline;text-underline-offset:4px;cursor:pointer;pointer-events:auto;color:#E6EDF5">what is it?</a></div>';
              return big + link;
            },
            zIndex: 20,
          },
          pivot: { radius: 0 },
          // disable built-in dial; we render an overlay rim triangle
          dial: {
            radius: "0%",
            baseLength: "0%",
            rearLength: "0%",
            backgroundColor: "transparent",
            borderWidth: 0,
            topWidth: 0,
            baseWidth: 0,
          },
        },
      },

      series: [
        {
          type: "gauge",
          data: [0],
          yAxis: 0,
          animation: true,
        },
      ],

      tooltip: { enabled: false },
    };
  }, [clamped, size]);

  // Export methods
  const exportAsSVG = (): string | null => {
    if (!chartRef.current) return null;
    try {
      return (chartRef.current as any).getSVG();
    } catch (error) {
      console.error('Failed to export SVG:', error);
      return null;
    }
  };

  const exportAsPNG = (): string | null => {
    if (!chartRef.current) return null;
    try {
      // Returns base64 data URL (data:image/png;base64,...)
      return (chartRef.current as any).getImageDataURL();
    } catch (error) {
      console.error('Failed to export PNG:', error);
      return null;
    }
  };

  const downloadPNG = (filename = 'risk-score.png'): void => {
    if (!chartRef.current) return;
    try {
      (chartRef.current as any).exportChart({
        type: 'image/png',
        filename: filename.replace(/\.png$/, ''),
      });
    } catch (error) {
      console.error('Failed to download PNG:', error);
    }
  };

  const downloadSVG = (filename = 'risk-score.svg'): void => {
    if (!chartRef.current) return;
    try {
      (chartRef.current as any).exportChart({
        type: 'image/svg+xml',
        filename: filename.replace(/\.svg$/, ''),
      });
    } catch (error) {
      console.error('Failed to download SVG:', error);
    }
  };

  // animate 0 -> value (unchanged)
  const onChartInit = (chart: Highcharts.Chart) => {
    chartRef.current = chart;
    const p = chart.series?.[0]?.points?.[0];
    if (p) {
      p.update(0, true);
      setTimeout(
        () =>
          p.update(clamped, true, {
            duration: 2300,
          } as Partial<Highcharts.AnimationOptionsObject>),
        40,
      );
    }
    // start custom along-arc animation for the overlay pointer
    prevValRef.current = 0;
    animatePointerTo(clamped, 2300);
    // bind click to "what is it?" anchor
    bindExplainAnchor();
    
    // Provide export methods to parent component
    if (onExportReady) {
      onExportReady({
        exportAsSVG,
        exportAsPNG,
        downloadPNG,
        downloadSVG,
      });
    }
  };

  useEffect(() => {
    const c = chartRef.current;
    if (c && c.series?.[0]?.points?.[0]) {
      c.series[0].points[0].update(clamped, true, {
        duration: 2300,
      } as Partial<Highcharts.AnimationOptionsObject>);
    }
    // animate overlay pointer along arc
    animatePointerTo(clamped, 2300);
    // ensure anchor stays bound after redraws
    bindExplainAnchor();
  }, [clamped, size]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // restore "what is it?" click, now using class selector (no data-* attr)
  useEffect(() => {
    if (!onExplain) return;
    const c = chartRef.current;
    const el: HTMLElement | null = (c?.container as unknown as HTMLElement) || null;
    if (!el) return;
    const roots: (EventTarget | null)[] = [el, el.parentElement, document];
    const handler = (e: Event) => {
      const anyTarget = e.target as any;
      const anchor = anyTarget?.closest
        ? anyTarget.closest(".riskiq-explain-link")
        : anyTarget?.parentElement?.closest?.(".riskiq-explain-link");
      if (anchor) {
        e.preventDefault();
        e.stopPropagation();
        onExplain();
      }
    };
    roots.forEach((r) => r && r.addEventListener("click", handler, true));
    return () => {
      roots.forEach((r) => r && r.removeEventListener("click", handler, true));
    };
  }, [onExplain]);

  function bindExplainAnchor() {
    try {
      const c = chartRef.current;
      const el: HTMLElement | null = (c?.container as unknown as HTMLElement) || null;
      if (!el) return;
      const anchor = el.querySelector(
        "a.riskiq-explain-link",
      ) as HTMLElement | null;
      if (anchor && anchor !== explainAnchorRef.current) {
        if (explainAnchorRef.current) {
          explainAnchorRef.current.onclick = null as any;
        }
        explainAnchorRef.current = anchor;
        anchor.style.pointerEvents = "auto";
        anchor.style.cursor = "pointer";
        anchor.onclick = (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          onExplain && onExplain();
          return false;
        };
      }
    } catch {}
  }

  // Overlay rim-triangle pointer with light rounding, anchored to ring outer edge
  function drawOverlayPointer(
    chart: Highcharts.Chart,
    val: number,
    animate: boolean = false,
  ) {
    const paneAny = (chart as any).pane?.[0];
    const center: [number, number, number, number] | undefined = paneAny?.center;
    if (!center) return;
    const cx = chart.plotLeft + center[0];
    const cy = chart.plotTop + center[1];
    const Rp = center[2] / 2;
    const Rout = Rp * 0.97; // matches band outer
    const Rin = Rp * 0.86; // matches band inner
    const v = Math.max(0, Math.min(100, Number(val)));

    const axis: any = chart.yAxis && chart.yAxis[0];
    if (!axis) return;

    const startRad: number =
      typeof axis.startAngleRad === "number"
        ? axis.startAngleRad
        : (-120 * Math.PI) / 180;
    const endRad: number =
      typeof axis.endAngleRad === "number"
        ? axis.endAngleRad
        : (120 * Math.PI) / 180;
    const min: number = typeof axis.min === "number" ? axis.min : 0;
    const max: number = typeof axis.max === "number" ? axis.max : 100;
    const t = (v - min) / (max - min);
    const theta = startRad + (endRad - startRad) * t;
    const cos = Math.cos(theta),
      sin = Math.sin(theta);

    // increase tip inset toward center; now ~+20% beyond previous (overall ~1.92x base)
    const baseOvershoot = Math.round((6 * size) / 360);
    const overshoot = Math.max(
      baseOvershoot + 1,
      Math.round(baseOvershoot * 1.92),
    );
    const basePos = axis.getPosition ? axis.getPosition(v, Rout) : null;
    const tipPos = axis.getPosition ? axis.getPosition(v, Rin - overshoot) : null;
    const Bx = basePos ? basePos.x : cx + Rout * cos;
    const By = basePos ? basePos.y : cy + Rout * sin;
    const Tx = tipPos ? tipPos.x : cx + (Rin - overshoot) * cos;
    const Ty = tipPos ? tipPos.y : cy + (Rin - overshoot) * sin;

    const tx = -sin,
      ty = cos; // unit tangent
    // reduce base width ~20% from previous (net ~1.08x original)
    const halfW = Math.max(6, Math.round(((9 * size) / 360) * 1.08));
    const BLx = Bx - tx * halfW;
    const BLy = By - ty * halfW;
    const BRx = Bx + tx * halfW;
    const BRy = By + ty * halfW;

    const path: Highcharts.SVGPathArray = [
      ["M", BLx, BLy],
      ["L", BRx, BRy],
      ["L", Tx, Ty],
      ["Z"],
    ];
    if (!pointerRef.current) {
      pointerRef.current = chart.renderer
        .path(path)
        .attr({
          fill: "#FFFFFF",
          stroke: "#0B2740",
          "stroke-width": 2,
          "stroke-linejoin": "round",
          "stroke-linecap": "round",
          zIndex: 7,
        })
        .add();
    } else {
      if (animate) {
        pointerRef.current.animate(
          { d: path },
          { duration: 700 } as Partial<Highcharts.AnimationOptionsObject>,
        );
      } else {
        pointerRef.current.attr({ d: path } as Highcharts.SVGAttributes);
      }
    }
  }

  function animatePointerTo(target: number, duration = 700) {
    if (!chartRef.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const from = prevValRef.current;
    const to = Math.max(0, Math.min(100, target));
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = easeOutCubic(t);
      const cur = from + (to - from) * eased;
      drawOverlayPointer(chartRef.current!, cur, false);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        prevValRef.current = to;
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(step);
  }

  // NOTE: We keep labels in the same wrapper but pull them closer to the arc ends.
  // This is purely layout (no gauge logic touched).
  const labelBarStyle: React.CSSProperties = {
    marginTop: -85,
    padding: "0 8px",
    width: size,
  };

  return (
    <div
      className="flex flex-col items-center"
      style={{ width: size, height: size - Math.round(size * 0.12) }}
    >
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        callback={onChartInit}
      />
      <div
        className="w-full flex justify-between text-slate-500 font-barlow"
        style={labelBarStyle}
      >
        <div className="text-[12px]">{leftLabel}</div>
        <div className="text-[12px]">{rightLabel}</div>
      </div>
    </div>
  );
}
